import React, {
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { VStack, Text, LinkText } from '@adc/parallax-component-library';
import { SystemNote } from 'adc-sirius-module-common';
import i18n from '../../../utilities/i18n';
import './SystemNotes.scss';

type Props = {
  systemNotes: SystemNote[];
};

type ExpandableNoteProps = {
  systemNote: SystemNote;
  index: number;
};

const collapsedNoteHeight = 16 * 5; // line height * number of lines to show

const ExpandableNote = ({ systemNote, index }: ExpandableNoteProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const noteId = useId();
  const noteRef = useRef<HTMLPreElement>(null);

  useLayoutEffect(() => {
    setIsTruncated((noteRef.current?.scrollHeight || 0) > collapsedNoteHeight);
  }, [systemNote.note]);

  const noteContent = useMemo(() => {
    try {
      return JSON.stringify(JSON.parse(systemNote.note), null, 2);
    } catch {
      return systemNote.note;
    }
  }, [systemNote.note]);

  return (
    <VStack className="note-container" testID={`system-note-${index}`}>
      <pre
        id={noteId}
        ref={noteRef}
        className={`note${isExpanded ? ' expanded' : ''}`}
        data-testid={`system-note-pre-${index}`}
      >
        {new Date(systemNote.timestamp).toLocaleString()}
        {`\t${i18n.t('UploadHistory.source')}: ${systemNote.origin}\n`}
        <code>{noteContent}</code>
      </pre>
      {isTruncated && (
        <>
          {!isExpanded && <Text type="$bodySmall.default">...</Text>}
          <LinkText
            type="$bodySmall.default"
            onPress={() => setIsExpanded((expanded) => !expanded)}
            aria-controls={noteId}
            aria-expanded={isExpanded}
            marginVertical="$2"
            testID="expand-collapse-link"
          >
            {i18n.t(
              isExpanded ? 'UploadHistory.hideMore' : 'UploadHistory.showMore',
            )}
          </LinkText>
        </>
      )}
    </VStack>
  );
};

export const SystemNotes = ({ systemNotes }: Props) => {
  return (
    <VStack gap="$2" testID="system-notes">
      {systemNotes.map((systemNote, index) => (
        <ExpandableNote key={index} systemNote={systemNote} index={index} />
      ))}
    </VStack>
  );
};
